import { dark } from "@clerk/themes";

/**
 * Base Clerk appearance applied globally via <ClerkProvider>.
 * Safe for all Clerk surfaces: UserButton, UserProfile, and auth pages.
 *
 * Component-specific overrides:
 *   - UserButton / UserProfile: no additional overrides needed — the dark base
 *     theme and variable palette handle popover/modal backgrounds automatically.
 *   - SignIn / SignUp: extend this with `clerkAuthPageAppearance` below to add
 *     card transparency and hidden headers that only make sense on auth pages.
 */
export const clerkBaseAppearance = {
  baseTheme: dark,
  variables: {
    colorBackground: "transparent",
    colorInputBackground: "rgba(30, 41, 59, 0.8)",
    colorInputText: "#f8fafc",
    colorText: "#f8fafc",
    colorTextSecondary: "#cbd5e1",
    colorPrimary: "#6366f1",
    colorDanger: "#f87171",
    borderRadius: "0.5rem",
    fontFamily: "inherit",
  },
};

/**
 * Auth-page appearance for <SignIn> and <SignUp>.
 * Extends clerkBaseAppearance with overrides that are only appropriate
 * when Clerk renders inside the app's own styled card container:
 *   - Card background is transparent (the page card provides the background).
 *   - Clerk's default header/subtitle are hidden (the page renders its own).
 *   - rootBox is full-width to fill the container.
 */
export const clerkAuthPageAppearance = {
  ...clerkBaseAppearance,
  elements: {
    rootBox: "w-full",
    card: {
      boxShadow: "none",
      border: "none",
      padding: "1.25rem 1.75rem 1.75rem",
      background: "transparent",
      width: "100%",
    },
    headerTitle: "hidden",
    headerSubtitle: "hidden",
    socialButtonsBlockButton: {
      backgroundColor: "rgba(30, 41, 59, 0.9)",
      border: "1px solid rgba(100, 116, 139, 0.5)",
      color: "white",
      width: "100%",
      borderRadius: "0.5rem",
      padding: "0.625rem 1rem",
    },
    socialButtonsBlockButtonText: {
      color: "white",
      fontWeight: "500",
    },
    formButtonPrimary: "bg-indigo-600 hover:bg-indigo-500 text-white",
    formFieldInput:
      "border-slate-700/60 text-white placeholder-slate-500 focus:border-indigo-500/50 focus:ring-indigo-500/30",
    footer: {
      background: "transparent",
      borderTop: "1px solid rgba(71,85,105,0.4)",
    },
    footerAction: "!text-slate-300",
    footerActionText: { color: "#cbd5e1" },
    footerActionLink: "!text-indigo-400 hover:!text-indigo-300",
    dividerLine: "bg-slate-600",
    dividerText: "!text-slate-300",
    identityPreviewText: "text-slate-300",
    identityPreviewEditButton: "text-indigo-400",
    formFieldLabel: "!text-slate-300",
    formResendCodeLink: "text-indigo-400",
    otpCodeFieldInput: {
      backgroundColor: "rgba(30, 41, 59, 0.8)",
      border: "1px solid rgba(100, 116, 139, 0.6)",
      color: "#f8fafc",
      borderRadius: "0.5rem",
      outline: "none",
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    },
  },
};
